import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOData from '@/components/feature/SEOData';
import { useMemo } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const category = useMemo(() => {
    if (!article) return null;
    return categories.find((c) => c.slug === article.category);
  }, [article]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbs = [
    { name: '首页', url: 'https://mamadepengyou.com' },
  ];
  if (category) {
    breadcrumbs.push({
      name: category.name,
      url: `https://mamadepengyou.com/category/${category.slug}`,
    });
  }
  breadcrumbs.push({
    name: article.title,
    url: `https://mamadepengyou.com/article/${article.slug}`,
  });

  return (
    <>
      <SEOData
        pageTitle={`${article.title} - 妈妈的朋友在线观看·完整版高清免费播放`}
        pageDescription={article.excerpt}
        canonicalPath={`/article/${article.slug}`}
        pageType="article"
        articleData={{
          title: article.title,
          author: article.author,
          publishDate: article.publishDate,
          imageUrl: article.coverImage,
        }}
        breadcrumbs={breadcrumbs}
        faqs={[
          {
            question: `${article.title}的观影体验如何？`,
            answer: `${article.title}获得了观众的广泛好评，文章从多维度进行了深度解析，推荐在安静的环境下细细品味。`,
          },
          {
            question: `在哪里可以看到${article.title}的完整内容？`,
            answer: `妈妈的朋友平台提供${article.title}的详细介绍和深度影评分析，帮助您全面了解这部作品。`,
          },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <article className="py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              {category && (
                <>
                  <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                    {category.name}
                  </Link>
                  <i className="ri-arrow-right-s-line text-xs" />
                </>
              )}
              <span className="text-foreground-800 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="text-foreground-300">|</span>
                <span>{article.publishDate}</span>
                <span className="text-foreground-300">|</span>
                <span>{article.readTime}</span>
              </div>
            </header>

            <div className="w-full h-[400px] md:h-[450px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-xl font-heading text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:shadow-md transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-full h-40 overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1.5">
                          {rel.publishDate}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}